import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styles from './SiteHeader.module.css';
import { useGetStarted } from './GetStartedContext';
import { trackEvent } from '../../utils/analytics';
import { ChevronIcon } from './MarketingIcons';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
  PRODUCT_AREAS,
  PRODUCT_MORE,
  PRODUCT_TEAMS,
  SOLUTION_ITEMS,
  PRIMARY_LINKS,
  isProductPath,
  isSolutionsPath,
} from './navConfig';

const LOGO_SRC = 'https://velox-network-media.s3.eu-west-2.amazonaws.com/Picture+2.png';
const DESKTOP_NAV = '(min-width: 900px)';
const CLOSE_DELAY_MS = 140;

const SiteHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const toggleRef = useRef(null);
  const headerRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const { openGetStarted } = useGetStarted();
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const isDesktop = useMediaQuery(DESKTOP_NAV);
  const productPanelId = useId();
  const solutionsPanelId = useId();

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenMenu(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        toggleRef.current?.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!openMenu) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };
    const onPointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) setOpenMenu(null);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [openMenu]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleGetStartedClick = (source) => {
    trackEvent('Marketing CTA', 'get_started_click', source);
    setIsMenuOpen(false);
    setOpenMenu(null);
    openGetStarted();
  };

  const showMenu = (id) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(id);
  };

  const scheduleHideMenu = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY_MS);
  };

  const toggleMenu = (id) => {
    clearTimeout(closeTimer.current);
    setOpenMenu((current) => (current === id ? null : id));
  };

  const hoverHandlers = (id) =>
    isDesktop && canHover
      ? {
          onMouseEnter: () => showMenu(id),
          onMouseLeave: scheduleHideMenu,
        }
      : {};

  return (
    <header
      ref={headerRef}
      className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}
    >
      <div className={`vxm-container ${styles.inner}`}>
        <Link to="/" className={styles.brand} aria-label="Velox Network home">
          <img src={LOGO_SRC} alt="" className={styles.logo} width="112" height="28" />
        </Link>

        <nav className={styles.desktopNav} aria-label="Primary">
          <div className={styles.menuItem} {...hoverHandlers('product')}>
            <button
              type="button"
              className={`${styles.navTrigger} ${
                openMenu === 'product' || isProductPath(location.pathname)
                  ? styles.navTriggerActive
                  : ''
              }`}
              aria-expanded={openMenu === 'product'}
              aria-controls={productPanelId}
              aria-haspopup="true"
              onClick={() => toggleMenu('product')}
            >
              Product
              <ChevronIcon className={styles.triggerChevron} />
            </button>
            <div
              id={productPanelId}
              className={`${styles.panel} ${styles.panelWide} ${
                openMenu === 'product' ? styles.panelOpen : ''
              }`}
              aria-hidden={openMenu !== 'product'}
            >
              <p className={styles.panelLabel}>Platform</p>
              <ul className={styles.areaGrid}>
                {PRODUCT_AREAS.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={styles.areaLink}>
                      <span className={styles.areaTitle}>{item.label}</span>
                      <span className={styles.areaBlurb}>{item.blurb}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <ul className={styles.panelFoot}>
                {[...PRODUCT_MORE, PRODUCT_TEAMS].map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={styles.footLink}>
                      <span className={styles.areaTitle}>{item.label}</span>
                      <span className={styles.areaBlurb}>{item.blurb}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={styles.menuItem} {...hoverHandlers('solutions')}>
            <button
              type="button"
              className={`${styles.navTrigger} ${
                openMenu === 'solutions' || isSolutionsPath(location.pathname)
                  ? styles.navTriggerActive
                  : ''
              }`}
              aria-expanded={openMenu === 'solutions'}
              aria-controls={solutionsPanelId}
              aria-haspopup="true"
              onClick={() => toggleMenu('solutions')}
            >
              Solutions
              <ChevronIcon className={styles.triggerChevron} />
            </button>
            <div
              id={solutionsPanelId}
              className={`${styles.panel} ${styles.panelNarrow} ${
                openMenu === 'solutions' ? styles.panelOpen : ''
              }`}
              aria-hidden={openMenu !== 'solutions'}
            >
              <p className={styles.panelLabel}>Who it is for</p>
              <ul className={styles.stackList}>
                {SOLUTION_ITEMS.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={styles.areaLink}>
                      <span className={styles.areaTitle}>{item.label}</span>
                      <span className={styles.areaBlurb}>{item.blurb}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {PRIMARY_LINKS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.desktopActions}>
          <Link to="/login" className="vxm-btn vxm-btn--ghost">
            Sign in
          </Link>
          <button
            type="button"
            className="vxm-btn vxm-btn--primary"
            onClick={() => handleGetStartedClick('header')}
          >
            Get started
          </button>
        </div>

        <button
          type="button"
          ref={toggleRef}
          className={styles.menuToggle}
          aria-expanded={isMenuOpen}
          aria-controls="vxm-mobile-menu"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span className={`${styles.bar} ${isMenuOpen ? styles.barTop : ''}`} />
          <span className={`${styles.bar} ${isMenuOpen ? styles.barHidden : ''}`} />
          <span className={`${styles.bar} ${isMenuOpen ? styles.barBottom : ''}`} />
        </button>
      </div>

      <div
        id="vxm-mobile-menu"
        className={`${styles.mobileMenu} ${isMenuOpen ? styles.mobileMenuOpen : ''}`}
        hidden={!isMenuOpen}
      >
        <div className={styles.mobileActions}>
          <button
            type="button"
            className="vxm-btn vxm-btn--primary"
            onClick={() => handleGetStartedClick('mobile_menu')}
          >
            Get started
          </button>
          <Link to="/login" className="vxm-btn vxm-btn--secondary">
            Sign in
          </Link>
        </div>

        <nav className={styles.mobileNav} aria-label="Mobile">
          <p className={styles.mobileGroupLabel}>Product</p>
          {PRODUCT_AREAS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={styles.mobileNavLink}
            >
              <span>{item.label}</span>
              <span className={styles.mobileNavBlurb}>{item.blurb}</span>
            </NavLink>
          ))}
          {PRODUCT_MORE.map((item) => (
            <NavLink
              key={`more-${item.to}`}
              to={item.to}
              className={styles.mobileNavLink}
            >
              <span>{item.label}</span>
              <span className={styles.mobileNavBlurb}>{item.blurb}</span>
            </NavLink>
          ))}

          <p className={styles.mobileGroupLabel}>Solutions</p>
          {SOLUTION_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={styles.mobileNavLink}
            >
              <span>{item.label}</span>
              <span className={styles.mobileNavBlurb}>{item.blurb}</span>
            </NavLink>
          ))}

          <p className={styles.mobileGroupLabel}>Company</p>
          {PRIMARY_LINKS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${styles.mobileNavLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default SiteHeader;
